import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { makeChancenRouter } from '@chancenwerk/client/make-chancen-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { ANY_CONTEXT } from '@adornis/users/db/a-roles.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import '@chancenwerk/client/components/chancen-component-auto-navigation.js';
import { CourseAbschluss } from '@chancenwerk/shared/courses/CourseAbschluss.js';
import { CourseChancenschule } from '@chancenwerk/shared/courses/CourseChancenschule.js';
import { CourseDigital } from '@chancenwerk/shared/courses/CourseDigital.js';
import { CourseIntensive } from '@chancenwerk/shared/courses/CourseIntensive.js';
import { CourseLernfoerderung } from '@chancenwerk/shared/courses/CourseLernfoerderung.js';
import { CourseLUS } from '@chancenwerk/shared/courses/CourseLUS.js';
import { ChancenPermission } from '@chancenwerk/shared/permissions.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-loader.js';
import '@chancenwerk/client/design/components/cr-sidebar.js';
import '@chancenwerk/client/design/components/fonts.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import '@chancenwerk/client/components/chancen-component-login-redirect.js';
import '@chancenwerk/client/pages/chancen-page-no-permission.js';
import '@chancenwerk/client/pages/chancen-page-not-found.js';
import './components/board-component-sidebar.js';
import { dirtyData } from './globalSubjects.js';
import './pages/board-page-attendance-export-history.js';
import './pages/board-page-attendance-export.js';
import './pages/board-page-attendance.js';
import './pages/board-page-course-details-chancenabschluss.js';
import './pages/board-page-course-details-chancenschule.js';
import './pages/board-page-course-details-digital-intensive-course-kpf.js';
import './pages/board-page-course-details-digital-intensive-course.js';
import './pages/board-page-course-details-intensive-course.js';
import './pages/board-page-course-details-lernfoerderung.js';
import './pages/board-page-course-details-lus.js';
import './pages/board-page-course-digital.js';
import './pages/board-page-kpf-management.js';
import './pages/board-page-overview.js';
import './pages/board-page-team-treffen-overview.js';
import './pages/board-page-team-treffen.js';
import './pages/board-page-week-overview.js';
import './pages/board-page-work-tracking-op.js';
import './pages/board-page-work-tracking.js';
import { BoardFirstLevelPage, BoardRoutes } from './routes.js';

function adjustViewport() {
  if (!window.visualViewport) {
    return;
  }

  const metaTag = document.querySelector('meta[name="viewport"]');
  if (metaTag && window.visualViewport.width * window.visualViewport.scale < 1200) {
    metaTag.content = metaTag.content.replace('initial-scale=1', 'initial-scale=0.7');
  } else {
    metaTag.content = metaTag.content.replace('initial-scale=0.7', 'initial-scale=1');
  }
}

// Run on load and resize
adjustViewport();
window.addEventListener('resize', adjustViewport);

const askDirtyRouting = async () => {
  console.log('Route CHANGE');
  console.log(dirtyData.value);
  if (dirtyData.value.some(d => d.isDirty())) {
    const res = await ChromiumDialog.confirm('Du hast ungespeicherte Änderungen. Willst du wirklich fortfahren?');
    if (res) {
      dirtyData.value.forEach(r => r.revert());
      dirtyData.next([]);
    }
    return res;
  }
  return true;
};

const router = makeChancenRouter(
  {
    root: {
      path: '/',
      render: () => {
        goTo(BoardRoutes.overview);
        return nothing;
      },
    },
    [BoardFirstLevelPage.OVERVIEW]: {
      path: `/${BoardFirstLevelPage.OVERVIEW}`,
      render: () => html`<board-page-overview></board-page-overview>`,
    },
    [BoardFirstLevelPage.DIGITAL]: {
      path: `/${BoardFirstLevelPage.DIGITAL}`,
      render: () => html`<board-page-course-digital></board-page-course-digital>`,
    },
    [BoardFirstLevelPage.KPF_MANAGEMENT]: {
      path: `/${BoardFirstLevelPage.KPF_MANAGEMENT}/:id`,
      render: ({ id }) => html`<board-page-kpf-management .kpfID=${id}></board-page-kpf-management>`,
    },
    [BoardFirstLevelPage.WEEK_OVERVIEW]: {
      path: `/${BoardFirstLevelPage.WEEK_OVERVIEW}`,
      render: () => html`<board-page-week-overview></board-page-week-overview>`,
    },
    [BoardFirstLevelPage.ATTENDANCE]: {
      path: `/${BoardFirstLevelPage.ATTENDANCE}`,
      render: () => html`<board-page-attendance></board-page-attendance>`,
    },
    [BoardFirstLevelPage.ATTENDANCE_EXPORT]: {
      path: `/${BoardFirstLevelPage.ATTENDANCE_EXPORT}`,
      render: () => html`<board-page-attendance-export></board-page-attendance-export>`,
    },
    [BoardFirstLevelPage.ATTENDANCE_EXPORT_HISTORY]: {
      path: `/${BoardFirstLevelPage.ATTENDANCE_EXPORT_HISTORY}`,
      render: () => html`<board-page-attendance-export-history></board-page-attendance-export-history>`,
    },
    'details-lernfoerderung': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseLernfoerderung._class}/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-lernfoerderung .kpfID=${id}></board-page-course-details-lernfoerderung>`,
      routeChange: askDirtyRouting,
    },
    'details-lus': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseLUS._class}/:id`,
      render: ({ id }) => html`<board-page-course-details-lus .kpfID=${id}></board-page-course-details-lus>`,
      routeChange: askDirtyRouting,
    },
    'details-intensive': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseIntensive._class}/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-intensive-course .kpfID=${id}></board-page-course-details-intensive-course>`,
      routeChange: askDirtyRouting,
    },
    'details-digital-kpf': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseDigital._class}KPF/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-digital-intensive-course-kpf
          .kpfID=${id}
        ></board-page-course-details-digital-intensive-course-kpf>`,
      routeChange: askDirtyRouting,
    },
    'details-digital': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseDigital._class}/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-digital-intensive-course
        .digitalCourseID=${id}
      ></board-page-course-course-details-digital-intensive-course>`,
      routeChange: askDirtyRouting,
    },
    'details-chancenschule': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseChancenschule._class}/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-chancenschule .kpfID=${id}></board-page-course-details-chancenschule>`,
      routeChange: askDirtyRouting,
    },
    'details-chancenabschluss': {
      path: `/${BoardFirstLevelPage.DETAILS}/${CourseAbschluss._class}/:id`,
      render: ({ id }) =>
        html`<board-page-course-details-chancenabschluss .kpfID=${id}></board-page-course-details-chancenabschluss>`,
      routeChange: askDirtyRouting,
    },
    'team-treffen': {
      path: `/team-treffen/:id`,
      render: ({ id }) => html`<board-page-team-treffen .tid=${id}></board-page-team-treffen>`,
    },
    'team-treffen-overview': {
      path: `/team-treffen`,
      render: () => html`<board-page-team-treffen-overview></board-page-team-treffen-overview>`,
    },
    'work-tracking': {
      path: `/work-tracking`,
      render: () => html`<board-page-work-tracking-op></board-page-work-tracking-op>`,
    },
    'work-tracking-single': {
      path: `/work-tracking/:id`,
      render: ({ id }) => html`<board-page-work-tracking .eid=${id}></board-page-work-tracking>`,
    },
  },
  {
    routerDocumentTitle: 'CHANCENboard',
    fallback: () => {
      return html`<chancen-page-no-permission></chancen-page-no-permission>`;
    },
  },
).init();

@customElement('main-element')
export class MainElement extends ChemistryLitElement {
  override styles() {
    return [{ ':host': { background: this.colors.neutral.shade(50) } }];
  }

  public override render() {
    return authenticationGuard(
      () => {
        return html`${permissionGuard(
          ChancenPermission.board.LOGIN_BOARD,
          () => html`
            <chancen-component-fab-task-bar></chancen-component-fab-task-bar>
            <cr-flex flex ${css({ height: '100%' })}>
              <cr-flex horizontal h100>
                <board-component-sidebar></board-component-sidebar>
                <cr-flex id="outer-flex" flex ${css({ overflowY: 'auto', position: 'relative' })}>
                  ${watch(router.outlet)}
                </cr-flex>
              </cr-flex>
            </cr-flex>
          `,
          {
            forbiddenTemplate: html`<chancen-page-no-permission></chancen-page-no-permission>`,
            context: ANY_CONTEXT,
          },
        )}`;
      },
      html`<chancen-component-login-redirect></chancen-component-login-redirect>`,
    );
  }
}
